import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, Shield, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SortablePhotoRowProps {
  id: string;
  url: string;
  isCover: boolean;
  isProof: boolean;
  isVerifying: boolean;
  rowBusy: boolean;
  proofEligible: boolean;
  onToggleProof: () => void;
  onDelete: () => void;
}

export function SortablePhotoRow({
  id,
  url,
  isCover,
  isProof,
  isVerifying,
  rowBusy,
  proofEligible,
  onToggleProof,
  onDelete,
}: SortablePhotoRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-2xl bg-white/[0.04] border border-white/[0.06] p-2 touch-none',
        isDragging && 'shadow-2xl shadow-black/50 bg-white/[0.08] scale-[1.02]'
      )}
    >
      {/* Drag handle */}
      <button
        type="button"
        aria-label="Drag to reorder"
        className="h-12 w-8 flex items-center justify-center text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing touch-none shrink-0"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-5 h-5" />
      </button>

      {/* Thumb with overlaid status */}
      <div className="relative shrink-0">
        <img
          src={url}
          alt=""
          className={cn('w-14 h-14 rounded-xl object-cover', isCover && 'ring-2 ring-active-gold')}
          draggable={false}
        />
        {isCover && (
          <span className="absolute -bottom-1 -left-1 bg-active-gold text-black text-[9px] font-bold px-1.5 py-0.5 rounded leading-none shadow-none ring-0">
            COVER
          </span>
        )}
        {isProof && (
          <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-active-gold text-black flex items-center justify-center shadow">
            <Shield className="w-3 h-3" strokeWidth={2.5} />
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0" />

      {/* Inline actions: shield + trash */}
      <div className="flex items-center gap-1.5 shrink-0">
        {proofEligible && (
          <button
            type="button"
            onClick={onToggleProof}
            disabled={rowBusy}
            aria-label={isProof ? 'Clear proof photo' : 'Set as proof photo'}
            className={cn(
              'h-11 w-11 rounded-full flex items-center justify-center transition-colors active:scale-95',
              isProof
                ? 'bg-active-gold text-black'
                : 'bg-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.1]',
              rowBusy && 'opacity-50'
            )}
          >
            {isVerifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" strokeWidth={2.2} />}
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete photo"
          className="h-11 w-11 rounded-full flex items-center justify-center bg-white/[0.04] text-destructive/80 hover:text-destructive hover:bg-destructive/10 transition-colors active:scale-95"
        >
          <Trash2 className="w-5 h-5" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
